import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { getToken, getMe } from './api';
import { applyDir, getLang, useLang } from './i18n';
import './styles/design-tokens.css';

// Auth
import ConfirmEmailPage from './components/auth/ConfirmEmailPage';
import ResetPasswordPage from './components/auth/ResetPasswordPage';
import PrivacyPage from './components/auth/PrivacyPage';
import NewPasswordPage from './components/auth/NewPasswordPage';

// Voice of Dubai player screens
import AuthScreen from './components/vod/AuthScreen';
import ExcursionListScreen from './components/vod/ExcursionListScreen';
import TourScreen from './components/vod/TourScreen';
import StepPage from './components/excursion/StepPage';
import PlanScreen from './components/vod/PlanScreen';

// Admin
import AdminPage from './components/admin/AdminPage';

// Guests hitting a protected URL — send to sign-up and remember the target
function GuestRedirect() {
  const location = useLocation();
  const target = location.pathname + location.search;
  return <Navigate to={`/signup?next=${encodeURIComponent(target)}`} replace />;
}

// Logged-in users hitting /login or /signup — respect ?next=
function AuthedRedirect() {
  const location = useLocation();
  const next = new URLSearchParams(location.search).get('next');
  return <Navigate to={next || '/excursions'} replace />;
}

export const AuthContext = React.createContext(null);

applyDir(getLang());

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const lang = useLang();

  useEffect(() => {
    const token = getToken();
    if (token) {
      getMe()
        .then(data => setUser(data.user))
        .catch(() => setUser(null))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  if (loading) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#FBF8F3' }}><img src="/emblem.png" alt="" style={{ width: 90, opacity: .8 }} /></div>;
  }

  return (
    <AuthContext.Provider value={{ user, setUser }}>
      <BrowserRouter>
        <Routes key={lang}>
          <Route path="/" element={<Navigate to={user ? '/excursions' : '/signup'} replace />} />

          {/* Auth */}
          <Route path="/signup" element={!user ? <AuthScreen mode="signup" /> : <AuthedRedirect />} />
          <Route path="/register" element={<Navigate to="/signup" replace />} />
          <Route path="/login" element={!user ? <AuthScreen mode="signin" /> : <AuthedRedirect />} />
          <Route path="/verify-email" element={<ConfirmEmailPage />} />
          <Route path="/confirm-email" element={<ConfirmEmailPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/new-password" element={<NewPasswordPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />

          {/* Player */}
          <Route path="/excursions" element={user ? <ExcursionListScreen /> : <GuestRedirect />} />
          <Route path="/excursion/:excursionId" element={user ? <TourScreen /> : <GuestRedirect />} />
          <Route path="/excursion/:excursionId/step/:stepNum" element={user ? <StepPage /> : <GuestRedirect />} />
          <Route path="/plan" element={user ? <PlanScreen /> : <GuestRedirect />} />
          <Route path="/walk/:walkId/step/:stepNum" element={user ? <StepPage /> : <GuestRedirect />} />
          <Route path="/cities" element={<Navigate to="/excursions" replace />} />

          {/* Admin */}
          <Route path="/admin/*" element={user?.is_admin ? <AdminPage /> : <Navigate to="/login?next=/admin" />} />

          <Route path="*" element={<Navigate to={user ? '/excursions' : '/signup'} replace />} />
        </Routes>
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

export default App;
